import React, { useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation.js';
import Footer from '../components/Footer.jsx';
import { homepageImages } from '../assets/homepage/index.js';
import { applyPageSeo } from '../utils/seo.js';

const NumberedCard = ({ item, index }) => (
  <div className="bg-bg-card rounded-2xl p-5 border border-border shadow-sm">
    <h3 className="text-lg font-black tracking-[-0.02em] text-text-primary mb-2">{item.title}</h3>
    <p className="text-text-secondary leading-relaxed text-sm">{item.description}</p>
  </div>
);

const AgenticDevPage = () => {
  const { t, currentLang } = useTranslation('agenticdev');

  useEffect(() => {
    applyPageSeo({ ...t.seo, pageId: 'agenticdev' }, currentLang);
  }, [t.seo, currentLang]);

  return (
    <div className="min-h-screen bg-bg-main text-text-primary">
      <section className="pt-20 pb-8 md:pt-24 md:pb-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-[1fr_0.82fr] gap-6 lg:gap-10 items-center">
            <div>
              <div className="editorial-kicker mb-4">{t.hero?.eyebrow}</div>
              <h1 className="text-4xl md:text-5xl font-black tracking-[-0.045em] leading-tight mb-4 text-text-primary">
                {t.hero?.title}
              </h1>
              <p className="text-xl md:text-2xl font-black tracking-[-0.025em] text-primary-dark mb-3">
                {t.hero?.claim}
              </p>
              <p className="text-base md:text-lg text-text-secondary leading-relaxed max-w-3xl">
                {t.hero?.lead}
              </p>
            </div>
            <div className="homepage-photo-frame h-[210px] md:h-[300px]">
              <img src={homepageImages.codeDesk} alt="" className="homepage-photo" aria-hidden="true" />
            </div>
          </div>
        </div>
      </section>

      <section className="homepage-section homepage-muted-panel">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl mb-8">
            <div className="editorial-kicker mb-5" aria-hidden="true" />
            <h2 className="text-4xl md:text-5xl font-black tracking-[-0.04em] text-text-primary mb-4">
              {t.implicitContextProblem?.title}
            </h2>
            <p className="text-lg text-text-secondary leading-relaxed">
              {t.implicitContextProblem?.intro}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 mb-8">
            {t.implicitContextProblem?.items?.map((item, index) => (
              <NumberedCard key={index} item={item} index={index} />
            ))}
          </div>

          <p className="site-statement site-statement--large">
            {t.implicitContextProblem?.resolution}
          </p>
        </div>
      </section>

      <section className="homepage-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-6 items-stretch">
            <div className="homepage-card p-7">
              <h2 className="text-3xl font-black tracking-[-0.04em] text-text-primary mb-4">
                {t.antiVibeCoding?.title}
              </h2>
              <p className="text-text-secondary leading-relaxed mb-5">
                {t.antiVibeCoding?.description}
              </p>
              <div className="space-y-3">
                {t.antiVibeCoding?.contrasts?.map((contrast, index) => (
                  <div key={index}>
                    <span className="text-text-secondary">{contrast}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="homepage-card p-7 bg-bg-hover">
              <h2 className="text-3xl font-black tracking-[-0.04em] text-text-primary mb-4">
                {t.principle?.title}
              </h2>
              <p className="text-xl font-black text-primary mb-4">
                {t.principle?.thesis}
              </p>
              <p className="text-text-secondary leading-relaxed">
                {t.principle?.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="homepage-section homepage-muted-panel">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <h2 className="text-4xl md:text-5xl font-black tracking-[-0.04em] text-text-primary mb-4">
              {t.principle?.pillarsTitle}
            </h2>
            <p className="text-lg text-text-secondary leading-relaxed">
              {t.principle?.pillarsIntro}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px border border-border bg-border rounded-2xl overflow-hidden">
            {t.principle?.pillars?.map((pillar, index) => (
              <div key={index} className="bg-bg-card p-6">
                <h3 className="text-xl font-black text-text-primary mb-2">{pillar.title}</h3>
                <p className="text-text-secondary text-sm leading-relaxed">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="homepage-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <div className="editorial-kicker mb-5" aria-hidden="true" />
              <h2 className="text-4xl md:text-5xl font-black tracking-[-0.04em] text-text-primary mb-4">
                {t.vault?.title}
              </h2>
              <p className="text-lg text-text-secondary leading-relaxed mb-6">
                {t.vault?.description}
              </p>
              <div className="bg-bg-card rounded-2xl p-5 border border-border shadow-sm">
                <p className="font-black text-primary mb-2">{t.vault?.rule}</p>
                <p className="text-text-secondary leading-relaxed">{t.vault?.effect}</p>
              </div>
            </div>

            <div className="bg-text-primary rounded-2xl p-6 shadow-professional border border-text-primary/10 overflow-x-auto">
              <pre className="text-sm md:text-base leading-relaxed text-primary-light font-mono whitespace-pre">
                {(t.vault?.treeLines || []).join('\n')}
              </pre>
            </div>
          </div>
        </div>
      </section>

      <section className="homepage-section homepage-muted-panel">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl mb-8">
            <div className="editorial-kicker mb-5" aria-hidden="true" />
            <h2 className="text-4xl md:text-5xl font-black tracking-[-0.04em] text-text-primary mb-4">
              {t.outputs?.title}
            </h2>
            <p className="text-lg text-text-secondary leading-relaxed">
              {t.outputs?.description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {t.outputs?.items?.map((item, index) => (
              <div key={index} className="homepage-card p-5">
                <h3 className="font-black text-text-primary mb-2">{item.file}</h3>
                <p className="text-primary font-bold mb-2">{item.result}</p>
                <p className="text-text-secondary text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="homepage-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="homepage-card overflow-hidden">
            <div className="p-6 md:p-8 border-b border-border bg-bg-hover">
              <h2 className="text-4xl md:text-5xl font-black tracking-[-0.04em] text-text-primary mb-4">
                {t.comparison?.title}
              </h2>
              <p className="text-lg text-text-secondary leading-relaxed max-w-4xl">
                {t.comparison?.intro}
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left min-w-[760px]">
                <thead>
                  <tr className="border-b border-border bg-bg-secondary/60">
                    {t.comparison?.columns?.map((column, index) => (
                      <th key={index} className="p-4 text-sm font-black text-text-primary">
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {t.comparison?.rows?.map((row, index) => (
                    <tr key={index} className="border-b border-border last:border-b-0">
                      <td className="p-4 font-black text-primary">{row.approach}</td>
                      <td className="p-4 text-text-secondary text-sm">{row.center}</td>
                      <td className="p-4 text-text-secondary text-sm">{row.strength}</td>
                      <td className="p-4 text-text-secondary text-sm">{row.limit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="p-6 bg-bg-hover">
              <p className="text-lg font-black text-text-primary leading-relaxed">
                {t.comparison?.summary}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="homepage-section homepage-muted-panel">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <div className="editorial-kicker mb-5" aria-hidden="true" />
              <h2 className="text-4xl md:text-5xl font-black tracking-[-0.04em] text-text-primary mb-4">
                {t.workflow?.title}
              </h2>
              <p className="text-lg text-text-secondary leading-relaxed mb-6">
                {t.workflow?.description}
              </p>
              <div className="space-y-4">
                {t.workflow?.steps?.map((step, index) => (
                  <div key={index} className="bg-bg-card p-4 rounded-2xl border border-border shadow-sm">
                    <div>
                      <h3 className="font-black text-text-primary mb-1">{step.title}</h3>
                      <p className="text-text-secondary text-sm leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="homepage-card p-6 md:p-8">
              <h2 className="text-3xl font-black tracking-[-0.04em] text-text-primary mb-5">
                {t.impact?.title}
              </h2>
              <p className="text-text-secondary leading-relaxed mb-5">
                {t.impact?.description}
              </p>
              <div className="space-y-3">
                {t.impact?.items?.map((item, index) => (
                  <div key={index}>
                    <span className="text-text-secondary">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="site-contact">
            <div>
              <h2 className="site-heading">{t.contact?.title}</h2>
              {t.contact?.description && (
                <p className="finance-cta__lead">{t.contact?.description}</p>
              )}
            </div>
            <a
              href={`mailto:${t.contact?.email}?subject=${encodeURIComponent(t.contact?.subject || '')}`}
              className="homepage-button-primary"
            >
              {t.contact?.button}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AgenticDevPage;
